import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

import { datadogRum, DefaultPrivacyLevel, type RumInitConfiguration } from '@datadog/browser-rum';
// sessionReplaySampleRate set to 100 to enable recording of all sessions
// This can be reduced in the future for production environment to reduce data usage
const DATADOG_RUM_DEFAULTS = {
  site: 'datadoghq.com' as const,
  service: 'further-pricing-assistant-frontend',
  sessionSampleRate: 100,
  sessionReplaySampleRate: 100,
  trackUserInteractions: true,
  trackViewsManually: true,
  defaultPrivacyLevel: DefaultPrivacyLevel.MASK_USER_INPUT,
  allowedTracingUrls: ['https://backend.prod.pa.talkfurther.com'],
} as const;

type DatadogRumUser = {
  id: string;
  email?: string;
  name?: string;
  groups?: unknown[];
  is_admin?: boolean;
  [key: string]: unknown;
};

type UseDatadogRumOptions = {
  user?: DatadogRumUser | null;
  impersonatedUser?: DatadogRumUser | null;
};

let hasInitializedRum = false;

const getRumInitConfigurationFromEnv = (): RumInitConfiguration | null => {
  const applicationId = process.env.REACT_APP_DATADOG_RUM_APPLICATION_ID;
  const clientToken = process.env.REACT_APP_DATADOG_RUM_CLIENT_TOKEN;
  if (!applicationId || !clientToken) return null;

  return {
    applicationId,
    clientToken,
    site: DATADOG_RUM_DEFAULTS.site,
    service: DATADOG_RUM_DEFAULTS.service,
    env: process.env.REACT_APP_DATADOG_RUM_ENV,
    version: process.env.REACT_APP_DATADOG_RUM_VERSION,

    sessionSampleRate: DATADOG_RUM_DEFAULTS.sessionSampleRate,
    sessionReplaySampleRate: DATADOG_RUM_DEFAULTS.sessionReplaySampleRate,
    defaultPrivacyLevel: DATADOG_RUM_DEFAULTS.defaultPrivacyLevel,

    trackUserInteractions: DATADOG_RUM_DEFAULTS.trackUserInteractions,
    trackResources: true,
    trackLongTasks: true,

    trackViewsManually: DATADOG_RUM_DEFAULTS.trackViewsManually,
  };
};

/**
 * Initializes Datadog Browser RUM (once) and (optionally) tracks SPA views via React Router.
 *
 * Environment variables:
 * - REACT_APP_DATADOG_RUM_APPLICATION_ID: Datadog RUM application ID
 * - REACT_APP_DATADOG_RUM_CLIENT_TOKEN: Datadog client token
 * - REACT_APP_DATADOG_RUM_ENV: environment name (prod/stage/dev)
 * - REACT_APP_DATADOG_RUM_VERSION: app version/release (optional)
 */
export const useDatadogRum = ({ user, impersonatedUser }: UseDatadogRumOptions = {}) => {
  const location = useLocation();
  const previousImpersonatedUserIdRef = useRef<string | null>(null);
  const previousImpersonatedUserRef = useRef<DatadogRumUser | null>(null);

  useEffect(() => {
    if (hasInitializedRum) return;

    const configuration = getRumInitConfigurationFromEnv();
    if (!configuration) return;

    datadogRum.init(configuration);
    hasInitializedRum = true;

    if ((configuration.sessionReplaySampleRate ?? 0) > 0) {
      datadogRum.startSessionReplayRecording();
    }
  }, []);

  useEffect(() => {
    if (!hasInitializedRum) return;

    if (user?.id) {
      datadogRum.setUser({ ...user, id: user.id });
    } else {
      datadogRum.clearUser();
    }
  }, [
    user?.email,
    user?.id,
    user?.is_admin,
    user?.groups,
    user?.name,
  ]);

  useEffect(() => {
    if (!hasInitializedRum) return;

    const nextImpersonatedId = impersonatedUser?.id ?? null;
    const previousImpersonatedId = previousImpersonatedUserIdRef.current;

    const actorUser = user?.id
      ? {
          id: user.id,
          email: user.email,
          name: user.name,
          groups: user.groups,
          is_admin: user.is_admin,
        }
      : undefined;

    if (impersonatedUser?.id) {
      datadogRum.setGlobalContextProperty('impersonated_user', {
        id: impersonatedUser.id,
        email: impersonatedUser.email,
        name: impersonatedUser.name,
      });
    } else {
      datadogRum.removeGlobalContextProperty('impersonated_user');
    }

    if (previousImpersonatedId !== nextImpersonatedId) {
      if (!previousImpersonatedId && nextImpersonatedId) {
        datadogRum.addAction('impersonation_start', {
          actor_user: actorUser,
          impersonated_user: {
            id: impersonatedUser?.id,
            email: impersonatedUser?.email,
            name: impersonatedUser?.name,
          },
        });
      } else if (previousImpersonatedId && !nextImpersonatedId) {
        datadogRum.addAction('impersonation_stop', {
          actor_user: actorUser,
          impersonated_user: {
            id: previousImpersonatedUserRef.current?.id ?? previousImpersonatedId,
            email: previousImpersonatedUserRef.current?.email,
            name: previousImpersonatedUserRef.current?.name,
          },
        });
      } else if (previousImpersonatedId && nextImpersonatedId) {
        datadogRum.addAction('impersonation_change', {
          actor_user: actorUser,
          previous_impersonated_user: {
            id: previousImpersonatedUserRef.current?.id ?? previousImpersonatedId,
            email: previousImpersonatedUserRef.current?.email,
            name: previousImpersonatedUserRef.current?.name,
          },
          impersonated_user: {
            id: impersonatedUser?.id,
            email: impersonatedUser?.email,
            name: impersonatedUser?.name,
          },
        });
      }

      previousImpersonatedUserIdRef.current = nextImpersonatedId;
      previousImpersonatedUserRef.current = impersonatedUser ?? null;
    }
  }, [impersonatedUser?.email, impersonatedUser?.id, impersonatedUser?.name]);

  useEffect(() => {
    if (!hasInitializedRum) return;

    if (!DATADOG_RUM_DEFAULTS.trackViewsManually) return;

    datadogRum.startView({ name: `${location.pathname}${location.search}${location.hash}` });
  }, [location.hash, location.pathname, location.search]);
};
